import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Play,
  CheckCircle,
  Clock,
  Globe,
  Award,
  BarChart,
  FileText,
  Users,
  Star,
  Share2,
  Heart,
  ShoppingCart,
  ChevronDown,
  ChevronUp,
  Lock,
  Unlock,
  Video,
  Loader2
} from 'lucide-react'
import VideoPlayer from '../components/shared/VideoPlayer'

// Declare Razorpay on window
declare global {
  interface Window {
    Razorpay: any
  }
}

interface Course {
  id: string
  title: string
  description: string
  instructor: string
  thumbnail: string
  price: number
  originalPrice: number
  rating: number
  totalRatings: number
  duration: string
  level: string
  category: string
  totalLessons: number
  totalStudents: number
  language: string
  lastUpdated: string
  certificate: boolean
  features: string[]
  curriculum: {
    title: string
    lessons: {
      id: string
      title: string
      duration: string
      type: 'video' | 'text' | 'quiz'
      preview?: boolean
    }[]
  }[]
  instructor_info: {
    name: string
    bio: string
    avatar: string
    experience: string
    students: number
    courses: number
    rating: number
  }
  reviews: {
    id: string
    user: string
    rating: number
    comment: string
    date: string
    avatar: string
  }[]
}

export default function CourseView() {
  const { id: courseId } = useParams()
  const navigate = useNavigate()
  const [course, setCourse] = useState<Course | null>(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [isEnrolled, setIsEnrolled] = useState(false)
  const [isInWishlist, setIsInWishlist] = useState(false)
  const [isInCart, setIsInCart] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null)
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]))

  useEffect(() => {
    if (courseId) {
      loadCourse()
    }
  }, [courseId])

  const loadCourse = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch(`/api/courses/${courseId}`, { headers })

      if (response.ok) {
        const data = await response.json()
        const courseData = data.course

        setIsEnrolled(courseData.isEnrolled || false)

        const savedCart = JSON.parse(localStorage.getItem('userCart') || '[]')
        const savedWishlist = JSON.parse(localStorage.getItem('userWishlist') || '[]')
        setIsInCart(savedCart.some((item: any) => item.id === courseData._id || item.id === courseData.id))
        setIsInWishlist(savedWishlist.some((item: any) => item.id === courseData._id || item.id === courseData.id))

        setCourse({
          id: courseData.id || courseData._id,
          title: courseData.title,
          description: courseData.description,
          instructor: courseData.instructor || 'Instructor',
          thumbnail: courseData.thumbnail || courseData.image || '',
          price: courseData.price,
          originalPrice: courseData.originalPrice || Math.round(courseData.price * 1.3),
          rating: courseData.rating?.average || 4.8,
          totalRatings: courseData.rating?.count || 124,
          duration: courseData.duration || '4 weeks',
          level: courseData.level || 'Beginner',
          category: courseData.category?.name || 'Beauty',
          totalLessons: courseData.sections?.reduce((acc: number, sec: any) => acc + sec.lessons.length, 0) || 0,
          totalStudents: courseData.enrollmentCount || 0,
          language: 'English',
          lastUpdated: new Date(courseData.updatedAt).toLocaleDateString(),
          certificate: true,
          features: [], // Removed hardcoded features
          curriculum: courseData.sections || [],
          instructor_info: {
            name: courseData.instructor || 'Instructor',
            bio: courseData.instructorBio || '',
            avatar: courseData.instructorAvatar || '',
            experience: '',
            students: courseData.enrollmentCount || 0,
            courses: 0,
            rating: 0
          },
          reviews: courseData.reviews || []
        })
      }
    } catch (error) {
      console.error('Error loading course:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true)
        return
      }

      const script = document.createElement('script')
      script.src = 'https://checkout.razorpay.com/v1/checkout.js'
      script.onload = () => resolve(true)
      script.onerror = () => resolve(false)
      document.body.appendChild(script)
    })
  }

  const handleEnrollNow = async () => {
    if (!course) return

    setProcessing(true)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        navigate('/user/login')
        setProcessing(false)
        return
      }

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript()
      if (!scriptLoaded) {
        alert('Failed to load payment gateway. Please try again.')
        setProcessing(false)
        return
      }

      // Get user data for prefill
      const userData = JSON.parse(localStorage.getItem('user') || '{}')

      // Create payment order
      const orderResponse = await fetch('/api/payments/create-single-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          courseIds: [course.id],
          amount: course.price
        })
      })

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json()
        throw new Error(errorData.error || 'Failed to create payment order')
      }

      const orderData = await orderResponse.json()

      // Razorpay checkout options
      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        name: "Sudha's Makeup Academy",
        description: `Enrollment for ${course.title}`,
        order_id: orderData.orderId,
        prefill: {
          name: userData?.name || '',
          email: userData?.email || '',
          contact: userData?.phone || '',
        },
        theme: {
          color: '#E11D48', // rose-600
        },
        handler: async (response: any) => {
          // Payment successful, verify and enroll
          await handlePaymentSuccess(response, orderData.paymentId)
        },
        modal: {
          ondismiss: () => {
            setProcessing(false)
          },
        },
      }

      const razorpay = new window.Razorpay(options)
      razorpay.on('payment.failed', (response: any) => {
        console.error('Payment failed:', response.error)
        alert(`Payment failed: ${response.error.description}`)
        setProcessing(false)
      })

      razorpay.open()
    } catch (error: any) {
      console.error('Enrollment error:', error)
      alert(error.message || 'An error occurred. Please try again.')
      setProcessing(false)
    }
  }

  const handlePaymentSuccess = async (razorpayResponse: any, paymentId: string) => {
    if (!course) return

    const token = localStorage.getItem('token')
    if (!token) return

    try {
      const response = await fetch('/api/payments/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          paymentId,
          razorpayPaymentId: razorpayResponse.razorpay_payment_id,
          razorpayOrderId: razorpayResponse.razorpay_order_id,
          razorpaySignature: razorpayResponse.razorpay_signature,
          courseIds: [course.id],
          amount: course.price
        }),
      })

      const data = await response.json()

      if (response.ok) {
        setIsEnrolled(true)
        // Navigate to success page or course learning page
        navigate(`/payment-success?payment_id=${data.paymentId}&razorpay_payment_id=${razorpayResponse.razorpay_payment_id}&enrolled=1&amount=${course.price}`)
      } else {
        alert(data.error || 'Payment verification failed. Please contact support.')
      }
    } catch (error) {
      console.error('Payment verification error:', error)
      alert('Payment verification failed. Please contact support.')
    } finally {
      setProcessing(false)
    }
  }

  const toggleSection = (index: number) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(index)) newExpanded.delete(index)
    else newExpanded.add(index)
    setExpandedSections(newExpanded)
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-rose-500 border-t-transparent rounded-full animate-spin"></div></div>
  if (!course) return null

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 pb-20">

      {/* HEADER REMOVED */}

      <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-12 gap-12">

        {/* LEFT COLUMN - CONTENT (8 cols) */}
        <div className="lg:col-span-8 space-y-10">

          {/* TITLE SECTION */}
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4 leading-tight">{course.title}</h1>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">{course.description}</p>

            <div className="flex flex-wrap items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                <span className="font-bold">{course.rating}</span>
                <div className="flex text-yellow-500"><Star className="w-3 h-3 fill-current" /><Star className="w-3 h-3 fill-current" /><Star className="w-3 h-3 fill-current" /><Star className="w-3 h-3 fill-current" /><Star className="w-3 h-3 fill-current" /></div>
                <span className="ml-1">({course.totalRatings} ratings)</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>{course.totalStudents.toLocaleString()} students</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                <span>{course.language}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Last updated {course.lastUpdated}</span>
              </div>
            </div>
          </div>

          {/* WHAT YOU'LL LEARN - REMOVED HARDCODED SECTION */}

          {/* CURRICULUM */}
          <div>
            <h2 className="text-2xl font-bold mb-6">Course Content</h2>
            <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
              <span>{course.curriculum.length} sections</span>
              <span>•</span>
              <span>{course.totalLessons} lectures</span>
              <span>•</span>
              <span>{course.duration} total length</span>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
              {course.curriculum.map((section, idx) => (
                <div key={idx} className="border-b border-slate-100 last:border-0">
                  <button
                    onClick={() => toggleSection(idx)}
                    className="w-full px-6 py-4 flex items-center justify-between bg-slate-50/50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3 font-semibold text-slate-800">
                      {expandedSections.has(idx) ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      {section.title}
                    </div>
                    <span className="text-sm text-slate-500">{section.lessons.length} lectures</span>
                  </button>

                  {expandedSections.has(idx) && (
                    <div className="bg-white">
                      {section.lessons.map((lesson, lIdx) => (
                        <div key={lIdx} className="px-6 py-3 flex items-center justify-between hover:bg-slate-50 group">
                          <div className="flex items-center gap-3">
                            {lesson.type === 'video' ? <Play className="w-4 h-4 text-slate-400" /> : <FileText className="w-4 h-4 text-slate-400" />}
                            <span className="text-slate-700 group-hover:text-rose-600 transition-colors">{lesson.title}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            {lesson.preview && (
                              <button
                                onClick={() => {
                                  setPreviewVideoUrl('https://www.youtube.com/watch?v=LDIOZ6z4QoY') // Mock
                                  setShowPreview(true)
                                }}
                                className="text-xs font-semibold text-rose-600 hover:underline"
                              >
                                Preview
                              </button>
                            )}
                            <span className="text-sm text-slate-400">{lesson.duration}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* INSTRUCTOR - REMOVED HARDCODED DETAILS */}
          {course.instructor_info.bio && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Instructor</h2>
              <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
                <div className="flex items-start gap-6">
                  <img src={course.instructor_info.avatar} alt={course.instructor_info.name} className="w-20 h-20 rounded-full object-cover border-2 border-slate-100" />
                  <div>
                    <h3 className="text-xl font-bold text-rose-600 mb-1">{course.instructor_info.name}</h3>
                    <p className="text-slate-500 mb-4">Instructor</p>
                    <p className="text-slate-600 leading-relaxed">{course.instructor_info.bio}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* RIGHT COLUMN - STICKY SIDEBAR (4 cols) */}
        <div className="lg:col-span-4 relative">
          <div className="sticky top-24 space-y-6">

            {/* ENROLLMENT CARD */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xl">
              {/* Video Preview Area */}
              <div className="relative aspect-video bg-slate-900 group cursor-pointer" onClick={() => {
                setPreviewVideoUrl('https://www.youtube.com/watch?v=LDIOZ6z4QoY')
                setShowPreview(true)
              }}>
                <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-60 transition-opacity" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 text-white fill-current ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white font-medium text-sm">Preview this course</div>
              </div>

              <div className="p-6">
                <div className="flex items-end gap-3 mb-6">
                  <span className="text-4xl font-bold text-slate-900">₹{course.price.toLocaleString()}</span>
                  {course.originalPrice > course.price && (
                    <span className="text-lg text-slate-400 line-through mb-1">₹{course.originalPrice.toLocaleString()}</span>
                  )}
                  <span className="text-green-600 font-bold mb-1 ml-auto">{Math.round(((course.originalPrice - course.price) / course.originalPrice) * 100)}% OFF</span>
                </div>

                <div className="space-y-3 mb-6">
                  {isEnrolled ? (
                    <button
                      onClick={() => navigate(`/user/courses/${courseId}/learn`)}
                      className="w-full py-3.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl transition-colors"
                    >
                      Go to Course
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleEnrollNow}
                        disabled={processing}
                        className="w-full py-3.5 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-colors shadow-lg shadow-rose-200 flex items-center justify-center gap-2"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          'Enroll Now'
                        )}
                      </button>
                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            if (isInCart) {
                              navigate('/user/cart')
                            } else {
                              // Add to cart
                              const cart = JSON.parse(localStorage.getItem('userCart') || '[]')
                              if (!cart.some((item: any) => item.id === course.id)) {
                                cart.push({
                                  id: course.id,
                                  title: course.title,
                                  price: course.price,
                                  originalPrice: course.originalPrice,
                                  thumbnail: course.thumbnail,
                                  instructor: course.instructor,
                                  quantity: 1
                                })
                                localStorage.setItem('userCart', JSON.stringify(cart))
                                setIsInCart(true)
                                // Dispatch event to update cart badge in header
                                window.dispatchEvent(new CustomEvent('cart-updated', { detail: { count: cart.length } }))
                              }
                            }
                          }}
                          className={`flex-1 py-3 border font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 ${isInCart ? 'border-green-500 text-green-600 bg-green-50' : 'border-slate-200 hover:bg-slate-50'}`}
                        >
                          <ShoppingCart className="w-4 h-4" /> {isInCart ? 'Go to Cart' : 'Add to Cart'}
                        </button>
                        <button
                          onClick={() => setIsInWishlist(!isInWishlist)}
                          className={`w-14 border font-semibold rounded-xl transition-colors flex items-center justify-center ${isInWishlist ? 'border-rose-500 text-rose-500 bg-rose-50' : 'border-slate-200 hover:bg-slate-50'}`}
                        >
                          <Heart className={`w-5 h-5 ${isInWishlist ? 'fill-current' : ''}`} />
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div className="space-y-4 text-sm text-slate-600">
                  <div className="flex items-center gap-3"><Video className="w-4 h-4" /> {course.duration} on-demand video</div>
                  <div className="flex items-center gap-3"><FileText className="w-4 h-4" /> {course.totalLessons} lessons</div>
                  <div className="flex items-center gap-3"><Award className="w-4 h-4" /> Certificate of completion</div>
                </div>
              </div>
            </div>

            {/* SHARE */}
            <div className="flex justify-center">
              <button className="text-sm font-medium text-slate-500 hover:text-slate-900 flex items-center gap-2 transition-colors">
                <Share2 className="w-4 h-4" /> Share this course
              </button>
            </div>

          </div>
        </div>

      </div>

      {/* VIDEO MODAL */}
      {showPreview && previewVideoUrl && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl bg-black rounded-2xl overflow-hidden relative">
            <button
              onClick={() => setShowPreview(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white z-10"
            >
              Close
            </button>
            <div className="aspect-video">
              <VideoPlayer videoUrl={previewVideoUrl} title="Preview" className="w-full h-full" autoplay />
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
